import { useState } from "react";
import { Link } from "wouter";
import { AppLayout } from "@/components/layout/AppLayout";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Send, Plus, Pencil, HeartPulse, Trash2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { OutreachTabs } from "@/components/outreach/OutreachTabs";
import { useBlastCampaigns, useBlastMutations, type BlastCampaign } from "@/hooks/useBlast";

/**
 * Statuses a campaign can be deleted in. Mirrors the detail page and the
 * server guard, which refuses to delete anything mid-send.
 */
const DELETABLE: BlastCampaign["status"][] = ["draft", "cancelled", "completed"];

const STATUS_STYLE: Record<BlastCampaign["status"], string> = {
  draft: "bg-muted text-muted-foreground",
  scheduled: "bg-indigo-100 text-indigo-700 border-indigo-300 dark:bg-indigo-900/30 dark:text-indigo-400 dark:border-indigo-800",
  sending: "bg-green-100 text-green-700 border-green-300 dark:bg-green-900/30 dark:text-green-400 dark:border-green-800",
  paused: "bg-yellow-100 text-yellow-700 border-yellow-300 dark:bg-yellow-900/30 dark:text-yellow-400 dark:border-yellow-800",
  completed: "bg-blue-100 text-blue-700 border-blue-300 dark:bg-blue-900/30 dark:text-blue-400 dark:border-blue-800",
  cancelled: "bg-red-100 text-red-600 border-red-200 dark:bg-red-900/30 dark:text-red-400 dark:border-red-900",
};

function fmtSchedule(iso: string, tz: string | null): string {
  return new Date(iso).toLocaleString("en-US", {
    timeZone: tz || "America/Chicago", month: "short", day: "numeric", hour: "numeric", minute: "2-digit",
  }) + " " + (tz === "America/New_York" ? "ET" : tz === "America/Denver" ? "MT" : tz === "America/Los_Angeles" ? "PT" : tz === "America/Phoenix" ? "MST" : "CT");
}

function CampaignRow({ c }: { c: BlastCampaign }) {
  const done = c.sent + c.replied;
  const progress = c.total > 0 ? Math.round(((done + c.failed + c.skipped) / c.total) * 100) : 0;
  const href = c.status === "draft" ? `/campaigns/${c.id}/edit` : `/campaigns/${c.id}`;
  const [confirmDelete, setConfirmDelete] = useState(false);
  const m = useBlastMutations();

  // Duplicates are the reason this is on the list rather than only inside the
  // campaign: you spot two identical names here, side by side, and the fix
  // should not require opening one to find out which is the empty one.
  const recipientCount = c.total;

  return (
    <>
    <Link href={href} className="group flex items-center gap-4 px-4 py-3 hover:bg-muted/40 transition-colors">
      <div className="min-w-0 flex-1">
        <p className="font-medium text-sm truncate flex items-center gap-1.5">
          {c.name}
          {c.status === "draft" && <Pencil className="w-3 h-3 text-muted-foreground" />}
          {c.client_name && <span className="text-muted-foreground font-normal">· {c.client_name}</span>}
        </p>
        <p className="text-xs text-muted-foreground mt-0.5">
          {c.status === "scheduled" && c.scheduled_start_at
            ? <span className="text-indigo-600 dark:text-indigo-400">Starts {fmtSchedule(c.scheduled_start_at, c.timezone)} · {c.total} recipients</span>
            : <>{c.total} recipients{done > 0 ? ` · ${done} sent` : ""}{c.replied > 0 ? ` · ${c.replied} replied` : ""}{c.failed > 0 ? ` · ${c.failed} failed` : ""}</>}
        </p>
      </div>
      {(c.status === "sending" || c.status === "paused") && (
        <div className="w-28 h-1.5 bg-muted rounded-full overflow-hidden shrink-0">
          <div className={cn("h-full rounded-full", c.status === "sending" ? "bg-green-500" : "bg-yellow-500")} style={{ width: `${progress}%` }} />
        </div>
      )}
      <Badge variant="outline" className={cn("text-[10px] shrink-0", STATUS_STYLE[c.status])}>{c.status}</Badge>
      {DELETABLE.includes(c.status) && (
        <button
          type="button"
          aria-label={`Delete campaign ${c.name}`}
          title="Delete campaign"
          // The whole row is a Link, so the click has to be stopped from
          // navigating before the dialog opens.
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); setConfirmDelete(true); }}
          className="shrink-0 p-1 rounded text-muted-foreground opacity-0 group-hover:opacity-100 focus-visible:opacity-100 hover:text-red-600 hover:bg-red-50 transition-opacity dark:text-red-400 dark:bg-red-950/40"
        >
          <Trash2 className="w-4 h-4" />
        </button>
      )}
    </Link>
    <ConfirmDialog
      open={confirmDelete}
      onOpenChange={setConfirmDelete}
      title="Delete campaign"
      description={
        recipientCount > 0
          ? `Delete "${c.name}"? This removes ${recipientCount} recipients and any drafts written for them. This cannot be undone.`
          : `Delete "${c.name}"? It has no recipients. This cannot be undone.`
      }
      confirmLabel="Delete"
      variant="destructive"
      isLoading={m.deleteCampaign.isPending}
      onConfirm={async () => { await m.deleteCampaign.mutateAsync(c.id); setConfirmDelete(false); }}
    />
    </>
  );
}

export default function Campaigns() {
  const { data: campaigns = [], isLoading } = useBlastCampaigns();

  const active = campaigns.filter((c) => c.status === "sending").length;
  const scheduled = campaigns.filter((c) => c.status === "scheduled").length;
  const drafts = campaigns.filter((c) => c.status === "draft").length;
  const totalSent = campaigns.reduce((s, c) => s + c.sent + c.replied, 0);
  const totalReplied = campaigns.reduce((s, c) => s + c.replied, 0);

  const stat = (value: number | string, label: string, tone = "") => (
    <div className="flex-1 min-w-[110px] bg-card border rounded-lg px-4 py-3">
      <p className={cn("text-xl font-semibold tabular-nums", tone)}>{value}</p>
      <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground mt-0.5">{label}</p>
    </div>
  );

  return (
    <AppLayout title="Campaigns">
      <div className="space-y-4 page-enter">
        <h1 className="sr-only">Campaigns</h1>
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <OutreachTabs active="campaigns" />
          <div className="flex items-center gap-2">
            <Link href="/campaigns/mailboxes">
              <Button variant="outline" className="gap-1.5"><HeartPulse className="w-4 h-4" /> Mailbox health</Button>
            </Link>
            <Link href="/campaigns/new">
              <Button className="gap-1"><Plus className="w-4 h-4" /> New campaign</Button>
            </Link>
          </div>
        </div>

        {campaigns.length > 0 && (
          <div className="flex gap-3 flex-wrap">
            {stat(active, "Sending now", active > 0 ? "text-green-600 dark:text-green-400" : "")}
            {stat(scheduled, "Scheduled", scheduled > 0 ? "text-indigo-600 dark:text-indigo-400" : "")}
            {stat(drafts, "Drafts")}
            {stat(totalSent.toLocaleString(), "Emails sent")}
            {stat(totalReplied.toLocaleString(), "Replies", totalReplied > 0 ? "text-blue-600 dark:text-blue-400" : "")}
          </div>
        )}

        {isLoading ? (
          <Skeleton className="h-40" />
        ) : campaigns.length === 0 ? (
          <div className="text-center py-14 border rounded-lg">
            <Send className="w-10 h-10 text-muted-foreground/30 mx-auto mb-3" />
            <p className="font-medium">No campaigns yet</p>
            <p className="text-sm text-muted-foreground mt-1 max-w-md mx-auto">
              Blast a teaser to a client's tracked buyers, or run market outreach to prospects with AI-personalized emails. Sending rotates automatically across the firm's outreach mailboxes.
            </p>
            <Link href="/campaigns/new"><Button className="mt-4 gap-1"><Plus className="w-4 h-4" /> New campaign</Button></Link>
          </div>
        ) : (
          <div className="border rounded-lg divide-y">
            {campaigns.map((c) => <CampaignRow key={c.id} c={c} />)}
          </div>
        )}
      </div>
    </AppLayout>
  );
}
